import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="tooltip"
export default class TooltipController extends Controller {
  static translatedTitles = new Map();
  connect() {
    // Long press owns the shared tooltip on elements using both controllers.
    if (this.element.dataset.controller?.split(/\s+/).includes("long-press")) return;
    const isDesktop = globalThis.AppConfig.isDesktop;
    const showOnMobile = this.element.dataset?.showOnMobile === "true";

    // Don't enable tooltips on mobile unless explicitly enabled
    if (!isDesktop && !showOnMobile) return;

    this.initializeTooltip();
  }

  initializeTooltip(options = {}) {
    const showOnMobile = this.element.dataset?.showOnMobile === "true";
    document.addEventListener("click", this.closeTooltip);

    $(".tooltip").remove();

    // Protect the generated tooltip before Bootstrap inserts it into the page.
    const template = document.createElement("div");
    template.innerHTML = this.element.dataset.bsTemplate || bootstrap.Tooltip.Default.template;
    for (const node of template.querySelectorAll(".tooltip, .tooltip-inner")) {
      node.classList.add("notranslate");
      node.setAttribute("translate", "no");
    }

    this.tooltip = new bootstrap.Tooltip(this.element, {
      boundary: document.body,
      sanitize: false,
      customClass: [this.element.dataset.bsCustomClass, "notranslate"].filter(Boolean).join(" ").trim().replace(/\s+/g, " "),
      template: template.innerHTML,
      trigger: showOnMobile ? "hover focus click" : "hover focus",
      ...options
    });

    this.element.addEventListener("show.bs.tooltip", this.prepareTranslatedTitle);
    this.element.addEventListener("inserted.bs.tooltip", this.translateVisibleTooltip);
    document.addEventListener("itoons:language-change", this.languageChanged);
    this.prepareTranslatedTitle();
  }

  disconnect() {
    this.element.removeEventListener("show.bs.tooltip", this.prepareTranslatedTitle);
    this.element.removeEventListener("inserted.bs.tooltip", this.translateVisibleTooltip);
    document.removeEventListener("itoons:language-change", this.languageChanged);

    document.removeEventListener("click", this.closeTooltip);

    const tooltip = this.tooltip;
    this.tooltip = null;

    if (!tooltip) return;

    const dispose = () => {
      if (
        globalThis.bootstrap?.Tooltip.getInstance(this.element) === tooltip
      ) {
        tooltip.dispose();
      }
    };

    // Bootstrap's hide callback reads _activeTrigger. Disposing during the
    // transition nulls that object and causes `_isWithActiveTrigger` to throw.
    if (this.element.hasAttribute("aria-describedby")) {
      this.element.addEventListener("hidden.bs.tooltip", dispose, {
        once: true
      });
      tooltip.hide();
      setTimeout(dispose, 1000);
    } else {
      dispose();
    }
  }

  closeTooltip = () => {
    this.tooltip?.hide();
  };

  languageChanged = () => {
    this.closeTooltip();
    this.prepareTranslatedTitle();
  };

  translateVisibleTooltip = async () => {
    const language = localStorage.getItem("selected_lang") || "en";
    const tooltip = this.tooltip;
    const tip = tooltip?.tip;
    if (!tip || language === "en" || this.element.dataset.translationTitleLanguage === language) return;

    const inner = tip.querySelector(".tooltip-inner");
    const block = document.getElementById("google-translate-block");
    const translator = block && this.application.getControllerForElementAndIdentifier(block, "google-translate");
    if (!inner || !translator) return;

    // Translate the actual tooltip: the widget can skip offscreen staging text.
    // Keep the tooltip open while masking its source-language text.
    inner.style.setProperty("color", "transparent", "important");
    tip.classList.add("tooltip-translation-loading");
    tip.setAttribute("aria-busy", "true");
    const source = this.element.dataset.translationTitleSource || inner.textContent;
    const translated = translator.waitForTranslationDone({ root: inner, requireMutation: true });
    for (const node of [tip, inner]) {
      node.classList.remove("notranslate");
      node.setAttribute("translate", "yes");
    }
    try {
      const ready = await translated;
      if (ready && this.tooltip === tooltip &&
          language === (localStorage.getItem("selected_lang") || "en")) {
        const title = inner.textContent;
        this.element.dataset.translationTitleSource = source;
        this.element.dataset.translationTitleLanguage = language;
        this.element.dataset.translationTitleText = title;
        this.element.dataset.bsOriginalTitle = title;
        if (Object.hasOwn(this.element.dataset, "bsTitle")) this.element.dataset.bsTitle = title;
        tooltip._config.title = title;
        TooltipController.translatedTitles.set(JSON.stringify([language, source]), title);
      }
    } finally {
      inner.style.removeProperty("color");
      tip.classList.remove("tooltip-translation-loading");
      tip.setAttribute("aria-busy", "false");
      for (const node of [tip, inner]) {
        node.classList.add("notranslate");
        node.setAttribute("translate", "no");
      }
    }
  };

  prepareTranslatedTitle = () => {
    const language = localStorage.getItem("selected_lang") || "en";
    if (!this.tooltip) return;
    const source = this.element.dataset.translationTitleSource ||
      this.element.dataset.bsOriginalTitle || this.element.dataset.bsTitle;
    if (!source) return;
    this.element.dataset.translationTitleSource = source;
    const title = language === "en" ? source :
      (this.element.dataset.translationTitleLanguage === language && this.element.dataset.translationTitleText) ||
      TooltipController.translatedTitles.get(JSON.stringify([language, source]));
    if (!title) {
      this.element.dataset.translationTitleLanguage = "";
      this.element.dataset.bsOriginalTitle = source;
      if (Object.hasOwn(this.element.dataset, "bsTitle")) this.element.dataset.bsTitle = source;
      this.tooltip._config.title = source;
      return;
    }
    this.element.dataset.translationTitleLanguage = language;
    this.element.dataset.translationTitleText = title;
    this.element.dataset.bsOriginalTitle = title;
    if (Object.hasOwn(this.element.dataset, "bsTitle")) this.element.dataset.bsTitle = title;
    this.tooltip._config.title = title;
  };
}
